import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { programsApi, categoriesApi, pagesApi } from "../services/api";
import HeroSection from "../components/HeroSection";

function ProgramsPage() {
  const [categories, setCategories] = useState([]);
  const [categoriesMap, setCategoriesMap] = useState({}); // Map for quick lookup
  const [programs, setPrograms] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pageData, setPageData] = useState({});
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    loadData();
    fetchPageData();
  }, []);

  const fetchPageData = async () => {
    try {
      const response = await pagesApi.getByPageType("programs");
      setPageData(response.data || {});
    } catch (error) {
      console.error("Error loading programs page data:", error);
    }
  };

  useEffect(() => {
    if (pageData?.use_slider && pageData?.slider_images?.length > 0) {
      const interval = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % pageData.slider_images.length);
      }, pageData.slider_interval || 5000);
      return () => clearInterval(interval);
    }
  }, [pageData]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch categories
      const categoriesResponse = await categoriesApi.getAll();
      const categoriesData = Array.isArray(categoriesResponse.data)
        ? categoriesResponse.data
        : [];

      // Create a map for quick category name lookup
      const catMap = {};
      categoriesData.forEach((cat) => {
        catMap[cat.id] = cat.name;
      });
      setCategoriesMap(catMap);

      // Fetch all programs from API
      const programsResponse = await programsApi.getAll(false);
      const programsData = Array.isArray(programsResponse.data)
        ? programsResponse.data
        : [];

      setPrograms(programsData);

      // Create category options with names from API
      const categoryList = [
        { id: "all", name: "All Programs" },
        ...categoriesData.map((cat) => ({
          id: cat.id,
          name: cat.name,
        })),
      ];

      setCategories(categoryList);
    } catch (err) {
      console.error("Error loading data:", err);
      setError(
        "Failed to load programs and categories. Please try again later.",
      );
      // Fallback to empty arrays
      setCategories([{ id: "all", name: "All Programs" }]);
      setPrograms([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredPrograms =
    selectedCategory === "all"
      ? programs
      : programs.filter((p) => p.category_id == selectedCategory);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600 mb-4"></div>
          <p className="text-gray-600">Loading programs...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="text-4xl mb-4">⚠️</div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Error Loading Programs
          </h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={loadData}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <HeroSection
        title={
          pageData?.hero_headline ||
          pageData?.title ||
          "Become a better leader, transform your business"
        }
        subtitle={
          pageData?.hero_subheadline ||
          pageData?.meta_description ||
          pageData?.content ||
          "Turn people into assets, drive growth with innovation"
        }
        heroImage={pageData?.hero_image}
        useSlider={!!pageData?.use_slider}
        sliderImages={pageData?.slider_images || []}
        currentSlide={currentSlide}
        onSlideChange={setCurrentSlide}
        ctaText={pageData?.cta_text}
        ctaHref={pageData?.cta_link || "/contact"}
      />

      {/* Categories */}
      <section className="py-8 border-b bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap gap-2 justify-center">
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setSelectedCategory(cat.id)}
                className={`px-6 py-2 rounded-full transition ${
                  selectedCategory === cat.id
                    ? "bg-blue-600 text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                {cat.name}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Program Listings */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900">
              {selectedCategory === "all"
                ? "All Programs"
                : categories.find((c) => c.id === selectedCategory)?.name}
            </h2>
            <p className="text-gray-600">
              {filteredPrograms.length} program
              {filteredPrograms.length !== 1 ? "s" : ""} found
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredPrograms.map((program) => (
              <div
                key={program.id}
                className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition duration-300"
              >
                <div className="p-6">
                  <div className="flex items-start mb-4">
                    <div className="text-3xl mr-4">{program.icon || "📚"}</div>
                    <div>
                      {!!program.featured && (
                        <span className="inline-block bg-purple-100 text-purple-800 text-xs font-semibold px-3 py-1 rounded-full mb-2">
                          Featured
                        </span>
                      )}
                      <h3 className="text-xl font-bold text-gray-900">
                        {program.title}
                      </h3>
                    </div>
                  </div>

                  <p className="text-gray-600 mb-6">
                    {program.description ||
                      program.detailed_content?.substring(0, 150)}
                  </p>

                  <div className="flex justify-between items-center mb-6">
                    <div>
                      {program.duration && (
                        <>
                          <span className="text-blue-600 font-semibold">
                            {program.duration}
                          </span>
                          <span className="text-gray-500 mx-2">•</span>
                        </>
                      )}
                      {Boolean(program.show_price) && (
                        <span className="text-gray-700 font-semibold">
                          {program.price != 0
                            ? `BDT ${program.price}`
                            : "Contact for pricing"}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-500 capitalize">
                      {categoriesMap[program.category_id] || "Uncategorized"}
                    </span>
                    <Link
                      to={`/programs/${program.slug}`}
                      className="text-blue-600 font-semibold hover:text-blue-800 hover:underline"
                    >
                      Learn More →
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Empty State */}
          {filteredPrograms.length === 0 && (
            <div className="text-center py-12">
              <div className="text-gray-400 text-4xl mb-4">📚</div>
              <h3 className="text-lg font-semibold text-gray-700 mb-2">
                No programs found
              </h3>
              <p className="text-gray-500">
                Try selecting a different category
              </p>
            </div>
          )}
        </div>
      </section>

      {/* CTA Strip */}
      <section className="bg-blue-600 text-white py-12">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl font-bold mb-4">
            Want to learn more about our programs?
          </h2>
          <p className="mb-6 text-blue-100">
            Contact us for detailed program outlines, schedules, and enrollment
            information.
          </p>
          <Link
            to="/contact"
            className="inline-block bg-white text-blue-600 px-8 py-3 rounded-full font-semibold hover:bg-gray-100 transition"
          >
            Contact us for details
          </Link>
        </div>
      </section>
    </div>
  );
}

export default ProgramsPage;
